import { useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import { useToast } from "../../contexts/ToastContext";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";

import DateRangeFilter from "../../components/filterDropdown/DateRangeFilter";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import { orderStatusOptions } from "../../shared/utility";
import ImportMediaCsvDropzone from "../../components/importMedia/ImportMediaCsvDropzone";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";

export function ManageOrdersViewModel(logout) {
  const { showSuccess } = useToast();
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [filters, setFilters] = useState({
    customer: { value: -1, label: "All Customers" },
    orderStatus: { value: -1, label: "All Orders" },
    employee: { value: -1, label: "All Employees" },
    startDate: null,
    endDate: null,
  });
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState({ data: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getOrders = async function (search, page, filterArg) {
    setLoading(true);
    const payload = {
      page: page,
      filters: { ...filterArg, search: search },
    };
    let callResult = await makePostApiCall("fetchOrders", payload);

    if (callResult.success) {
      setOrders(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    setLoading(false);
  };

  const deleteOrder = async function (id) {
    let callResult = await makePostApiCall(`/deleteOrder/${id}`);

    if (callResult.success) {
      const filteredItems = orders.data.filter((order) => order.id !== id);
      setOrders({ ...orders, data: filteredItems });

      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      showSuccess(
        callResult.data.message || "Action completed"
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const askDeleteOrder = function (order) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteOrder(order.id)}
          message={`Are you sure you want to delete ${order.po_number}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const getCustomersForDropdown = async function () {
    let callResult = await makePostApiCall(`getCustomersForDropdown`);
    if (callResult.success) {
      setCustomers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const getUsersForDropdown = async function () {
    let callResult = await makePostApiCall(`usersForDropdown`);
    if (callResult.success) {
      setEmployees(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const resetFilters = () => {
    const filterReset = {
      customer: { value: -1, label: "All Customers" },
      orderStatus: { value: -1, label: "All Orders" },
      employee: { value: -1, label: "All Employees" },
      startDate: null,
      endDate: null,
    };
    setFilters(filterReset);

    setShowFilters(false);
    getOrders(null, 1, filterReset);
  };

  const uploadOrderCsv = async function (uploadedFile) {
    const { name, value } = uploadedFile.target;
    const payload = { [name]: value };
    let callResult = await makePostApiCall(
      "orders/import",
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      getOrders(null, 1, filters);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const openImportOrder = () => {
    setEmptyModelOpen({
      childComponent: (
        <ImportMediaCsvDropzone
          fileName="orderCsv"
          actionCall={uploadOrderCsv}
          setOpen={setEmptyModelOpen}
          title="Upload CSV to import orders"
        />
      ),
      open: true,
    });
  };

  const filterComponents = [
    <DateRangeFilter
      endDate={filters.endDate}
      onChange={(name, value) =>
        setFilters({
          ...filters,
          [name]: value,
        })
      }
      resetFilter={() =>
        setFilters({
          ...filters,
          startDate: null,
          endDate: null,
        })
      }
      startDate={filters.startDate}
    />,
  ];

  // Only show select filters for userType === 1
  const userType = Number(localStorage.getItem("userType"));
  if (userType === 1) {
    filterComponents.push(
      <FilterSingleSelectDropdown
        header="Order Status"
        onChange={(value) =>
          setFilters({
            ...filters,
            orderStatus: value,
          })
        }
        options={orderStatusOptions}
        resetFilter={() =>
          setFilters({
            ...filters,
            orderStatus: { value: -1, label: "All Orders" },
          })
        }
        valueName="orderStatus"
        value={filters.orderStatus}
      />
    );
    console.log(customers.customers);
    filterComponents.push(
      <FilterSingleSelectDropdown
        header="Customer"
        onChange={(value) =>
          setFilters({
            ...filters,
            customer: value,
          })
        }
        options={customers.customers}
        resetFilter={() =>
          setFilters({
            ...filters,
            customer: { value: -1, label: "All Customers" },
          })
        }
        valueName="customer"
        value={filters.customer}
      />
    );

    filterComponents.push(
      <FilterSingleSelectDropdown
        header="Employee"
        onChange={(value) =>
          setFilters({
            ...filters,
            employee: value,
          })
        }
        options={employees}
        resetFilter={() =>
          setFilters({
            ...filters,
            employee: { value: -1, label: "All Employees" },
          })
        }
        valueName="employee"
        value={filters.employee}
      />
    );
  }

  return {
    customers,
    employees,
    filterComponents,
    getCustomersForDropdown,
    getUsersForDropdown,
    getOrders,
    loading,
    orders,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    askDeleteOrder,
    filters,
    resetFilters,
    setFilters,
    setShowFilters,
    showFilters,
    openImportOrder,
  };
}
